import { Link } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import MuiLink from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  RATING_LABELS,
  carDetailsPath,
  displayName,
  ratingForScore,
  type ReliabilityDocument,
  type ReliabilityModel,
  type ReliabilityRating,
} from '../../domain/reliability';

const RATING_COLOR: Record<ReliabilityRating, 'success' | 'info' | 'default' | 'warning' | 'error'> = {
  excellent: 'success',
  good: 'info',
  average: 'default',
  'below-average': 'warning',
  poor: 'error',
};

export function ScoreChip({ score, size = 'small' }: { score: number | null; size?: 'small' | 'medium' }) {
  if (score === null) {
    return <Chip size={size} label="Not scored" variant="outlined" />;
  }
  const rating = ratingForScore(score);
  return <Chip size={size} color={RATING_COLOR[rating]} label={`${Math.round(score)} · ${RATING_LABELS[rating]}`} />;
}

/** Car name linking to its price/specs page when it's in our catalog. */
export function ModelName({ model, variant = 'body1' }: { model: ReliabilityModel; variant?: 'body1' | 'body2' | 'subtitle1' }) {
  const path = carDetailsPath(model);
  const name = displayName(model);
  return path ? (
    <MuiLink component={Link} to={path} variant={variant} fontWeight={600} underline="hover" color="text.primary">
      {name}
    </MuiLink>
  ) : (
    <Typography variant={variant} fontWeight={600} component="span">
      {name}
    </Typography>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Which report editions the data comes from, when it was last rebuilt, and coverage caveats. */
export function ReliabilitySources({ document }: { document: ReliabilityDocument }) {
  const { adac, tuev } = document.sources;
  return (
    <Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        {adac && (
          <Chip
            size="small"
            variant="outlined"
            component="a"
            href={adac.url ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            clickable={Boolean(adac.url)}
            label={`ADAC Pannenstatistik ${adac.reportYear ?? ''} · ${adac.entryCount} models`}
          />
        )}
        {tuev && (
          <Chip
            size="small"
            variant="outlined"
            component="a"
            href={tuev.url ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            clickable={Boolean(tuev.url)}
            label={`TÜV-Report ${tuev.reportYear ?? ''} · ${tuev.coverage === 'full' ? 'full table' : 'published rankings'}`}
          />
        )}
        <Typography variant="body2" color="text.secondary">
          Updated {formatDate(document.generatedAt)}
        </Typography>
      </Stack>
      {tuev?.coverage === 'partial' && (
        <Alert severity="info" sx={{ mt: 2 }}>
          The TÜV publishes only its best and worst models per age class for free. The full per-model TÜV table is sold
          in the AutoBild TÜV-Report, so TÜV figures here cover the published rankings, while ADAC figures cover
          every model in its statistics.
        </Alert>
      )}
    </Box>
  );
}

export function ReliabilityUnavailable() {
  return (
    <Alert severity="warning">
      Reliability data isn't available right now. It is refreshed automatically - please check back later.
    </Alert>
  );
}
